import { assert } from "@ember/debug";
import type JournalEvent from "parog-web/models/journal-event";

declare global {
  interface VitalSignRegistry {}
}

interface VitalSignParam<K extends keyof VitalSignRegistry> {
  key: K;
  journalEventType: typeof JournalEvent & { new (): VitalSignRegistry[K] };
  addMeasureForcId?: string;
  addMeasureForcIdMedic?: string;
  icon?: string;
  tooltipIcon?: string;
  showToPatient?: boolean;
  contextualDeclareRoute?: string;
}

export default class VitalSign<K extends keyof VitalSignRegistry> {
  key: K;
  journalEventType: { new (): VitalSignRegistry[K] };
  addMeasureForcId?: string;
  addMeasureForcIdMedic?: string;
  icon?: string;
  tooltipIcon?: string;
  showToPatient?: boolean;
  contextualDeclareRoute?: string;

  static _vitalSigns: Map<string, VitalSign<never>> = new Map();

  static findByJournalEventType<K extends keyof VitalSignRegistry>(
    eventType: string
  ): VitalSign<K> | undefined {
    return VitalSign._vitalSigns.get(eventType) as VitalSign<K>;
  }

  static findByKey<K extends keyof VitalSignRegistry>(key: K): VitalSign<K> {
    return (Array.from(VitalSign._vitalSigns.values()) as VitalSign<K>[]).find(
      vitalSign => vitalSign.key == key
    ) as VitalSign<K>;
  }

  get feature(): string | undefined {
    return this.addMeasureForcId?.split("/")[0];
  }

  get component(): string | undefined {
    return this.addMeasureForcId?.split("/")[1];
  }

  constructor({
    key,
    addMeasureForcId,
    addMeasureForcIdMedic,
    journalEventType,
    icon,
    tooltipIcon,
    showToPatient,
    contextualDeclareRoute
  }: VitalSignParam<K>) {
    this.key = key;
    this.addMeasureForcId = addMeasureForcId;
    this.addMeasureForcIdMedic = addMeasureForcIdMedic || addMeasureForcId;
    this.journalEventType = journalEventType;
    this.icon = icon;
    this.tooltipIcon = tooltipIcon;
    this.showToPatient = showToPatient;
    this.contextualDeclareRoute = contextualDeclareRoute;
    assert(
      "Journal Event type should be defined and should be a '*-journal-event' string",
      Boolean(journalEventType.type)
    );

    VitalSign._vitalSigns.set(
      journalEventType.type,
      this as unknown as VitalSign<never>
    );
  }
}
